import { useParams } from "react-router-dom";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Card } from "@/shared/components/ui/Card";
import { DetailSummary } from "@/shared/components/ui/DetailSummary";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader, PageHeaderAction } from "@/shared/components/ui/PageHeader";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Section } from "@/shared/components/ui/Section";
import { StatusBadge } from "@/shared/components/ui/StatusBadge";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useService } from "../hooks/serviceQueries";
import { formatServiceMinutes, formatServicePrice } from "../utils/serviceFormatters";
import { ServiceFormsSection } from "../components/ServiceFormsSection";

export function ServiceDetailsPage() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { goToServiceEdit } = useAppNavigation();
  const { business } = useCurrentBusiness();
  const { data: service, isLoading, error, refetch } = useService(serviceId);

  return (
    <Page>
      <PageHeader
        title={service?.name ?? "Service details"}
        actions={service && !error && <PageHeaderAction aria-label="Edit service" onClick={() => goToServiceEdit(service.id)}>Edit</PageHeaderAction>}
      />
      <PageContent className="space-y-6">
        {isLoading ? <RowSkeleton /> : error ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : !service ? <EmptyState message="Service not found" /> : (
          <>
            <Section title="Service details">
              <Card>
                <DetailSummary items={[
                  { label: "Availability", value: <StatusBadge tone={service.isActive ? "positive" : "neutral"}>{service.isActive ? "Active" : "Inactive"}</StatusBadge> },
                  { label: "Price", value: formatServicePrice(service.basePrice, business?.currency) },
                  {
                    label: "Color",
                    value: <span className="inline-flex items-center gap-2">
                      {service.color && <span aria-hidden="true" className="size-[22px] shrink-0 rounded-full border border-border" style={{ backgroundColor: service.color }} />}
                      {service.color || "Not set"}
                    </span>,
                  },
                ]} />
              </Card>
            </Section>
            <Section title="Description">
              <Card><p className="whitespace-pre-wrap text-sm leading-relaxed [overflow-wrap:anywhere]">{service.description || "No description added."}</p></Card>
            </Section>
            <Section title="Timing">
              <Card>
                <DetailSummary items={[
                  { label: "Duration", value: formatServiceMinutes(service.durationMinutes) },
                  { label: "Buffer before", value: formatServiceMinutes(service.bufferBeforeMinutes) },
                  { label: "Buffer after", value: formatServiceMinutes(service.bufferAfterMinutes) },
                ]} />
              </Card>
            </Section>
            <ServiceFormsSection formIds={service.formIds} attachedForms={service.forms} />
          </>
        )}
      </PageContent>
    </Page>
  );
}
